<script setup>
import { reactive, computed, watch, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import WhatsappChannel from 'dashboard/api/channel/whatsappChannel';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxDefault: useMapGetter('inboxes/getWhatsAppInboxDefault'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  campaignPlans: useMapGetter('campaignPlans/getCampaignPlans'),
};

const currentUser = computed(() => getters.getCurrentUser.value);

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  responseMessageText: null,
  followUpMessageText: null,
  followUpDelayMinutes: 60,
  selectedPlanId: null,
  actualVolume: 100,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const rules = computed(() => {
  const baseRules = {
    scheduledAt: { required },
  };

  if (state.selectedPlanId) {
    // Se plano selecionado, apenas volume é obrigatório
    return {
      ...baseRules,
      actualVolume: { required, minValue: minValue(1) },
    };
  }

  // Se novo plano, todos os campos são obrigatórios
  return {
    ...baseRules,
    title: { required, minLength: minLength(1) },
    inboxId: { required },
    templateId: { required },
    selectedAudience: { required },
  };
});

const v$ = useVuelidate(rules, state);

const isUsingPlan = computed(() => state.selectedPlanId !== null);

const campaignPlanOptions = computed(() => {
  const plans = formState.campaignPlans.value || [];
  return [
    { value: null, label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.PLAN_SELECTION.NEW_OPTION') },
    ...plans.map(plan => ({
      value: plan.id,
      label: plan.name,
      plan: plan,
    })),
  ];
});

const selectedPlan = computed(() => {
  if (!state.selectedPlanId) return null;
  return formState.campaignPlans.value?.find(p => p.id === state.selectedPlanId);
});

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

// Verificar se o template tem botões de resposta rápida (sim/não)
const hasQuickReplyButtons = computed(() => {
  if (!selectedTemplate.value) return false;
  const buttonComponent = selectedTemplate.value.components?.find(
    component => component.type === 'BUTTONS'
  );
  if (!buttonComponent?.buttons) return false;
  
  // Verificar se tem botões QUICK_REPLY
  const hasQuickReply = buttonComponent.buttons.some(
    button => button.type === 'QUICK_REPLY'
  );
  
  // Verificar se tem botão "Sim" ou "sim"
  const hasYesButton = buttonComponent.buttons.some(
    button => button.type === 'QUICK_REPLY' && 
    (button.text?.toLowerCase() === 'sim' || button.text?.toLowerCase() === 'yes' || button.text?.toLowerCase() === 'sí')
  );
  
  return hasQuickReply && hasYesButton;
});


const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  // Se está usando plano, não precisa validar parâmetros do template
  if (isUsingPlan.value) {
    return true;
  }
  // Parâmetros do template (body, header de mídia e botões) preenchidos
  return !templateParserRef.value?.isFormInvalid;
});

const isSubmitDisabled = computed(() => {
  // Se está usando plano, validar apenas os campos específicos
  if (isUsingPlan.value) {
    return v$.value.$invalid || isCreating.value;
  }
  // Se não está usando plano, validar tudo
  return v$.value.$invalid || !hasRequiredTemplateParams.value || isCreating.value;
});

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  if (formState.inboxDefault.value?.id) {
    state.inboxId = formState.inboxDefault.value.id;
  }
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  if (isUsingPlan.value && selectedPlan.value) {
    // Usando plano existente
    const plan = selectedPlan.value;
    
    // Gerar título automaticamente: "{nome_do_plano} dd/mm hh:mm"
    const scheduledDate = new Date(state.scheduledAt);
    const day = String(scheduledDate.getDate()).padStart(2, '0');
    const month = String(scheduledDate.getMonth() + 1).padStart(2, '0');
    const hours = String(scheduledDate.getHours()).padStart(2, '0');
    const minutes = String(scheduledDate.getMinutes()).padStart(2, '0');
    const formattedDateTime = `${day}/${month} ${hours}:${minutes}`;
    
    const planName = plan.name || 'Plano';
    const autoTitle = `${planName} ${formattedDateTime}`;
    
    // Usar rendered_template se disponível, senão usar nome do template
    const messageContent = plan.template_params?.rendered_template || 
                          `Template: ${templateName}`;
    
    return {
      title: autoTitle,
      message: messageContent,
      template_params: plan.template_params,
      inbox_id: plan.inbox_id,
      sender_id: currentUser.value?.id,
      scheduled_at: formatToUTCString(state.scheduledAt),
      audience: plan.audience,
      campaign_plan_id: plan.id,
      actual_volume: state.actualVolume,
    };
  }

  // Criando novo plano (fluxo atual)
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  const templateContent = parserData?.renderedTemplate || '';

  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    response_message_text: state.responseMessageText || null,
    follow_up_message_text: state.followUpMessageText || null,
    follow_up_delay_minutes: state.followUpDelayMinutes || null,
    inbox_id: state.inboxId,
    sender_id: currentUser.value?.id,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

const handleSubmit = async () => {
  if (isSubmitDisabled.value) return;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  // Pré-validação de limites antes de criar
  try {
    // Extrair a data de agendamento para validar os limites do dia correto
    const scheduledDate = state.scheduledAt
      ? new Date(state.scheduledAt).toISOString().split('T')[0]
      : null;

    const { data: limits } =
      await WhatsappChannel.getTemplateMessageLimits(scheduledDate);
    
    // Calcular volume real que será enviado
    let volumeToSend = 0;
    
    if (isUsingPlan.value) {
      // Se usando plano, usar o volume informado
      volumeToSend = state.actualVolume || 0;
    } else {
      // Se não usando plano, estimar tamanho da audiência disponível
      try {
        const { data } = await WhatsappChannel.estimateCampaignAudience(
          state.selectedAudience || []
        );
        volumeToSend = Number(data?.audience_count || 0);

        if (volumeToSend === 0) {
          useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_AUDIENCE_FULLY_IMPACTED'));
          return;
        }
      } catch (_) {
        volumeToSend = 0;
      }
    }
    
    // Determinar role baseado no tipo do usuário (mesma lógica do backend)
    const isSuperAdmin = currentUser.value?.type === 'SuperAdmin';
    const roleKey = isSuperAdmin ? 'super_admin' : 'manager';

    const totalRemaining = limits?.remaining?.total ?? 0;
    const roleRemaining = limits?.remaining?.[roleKey] ?? 0;

    // Validar que há saldo suficiente para o volume que será enviado
    if (totalRemaining < volumeToSend || roleRemaining < volumeToSend) {
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_LIMIT_REACHED'));
      return;
    }
  } catch (e) {
    // Se falhar ao obter limites, segue fluxo normal
  }

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Initialize inbox with default WhatsApp inbox
onMounted(() => {
  if (formState.inboxDefault.value?.id) {
    state.inboxId = formState.inboxDefault.value.id;
  }
});

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.responseMessageText = null;
    state.followUpMessageText = null;
    state.followUpDelayMinutes = 60;
  }
);

// Reset response template when main template changes
watch(
  () => state.templateId,
  () => {
    if (!hasQuickReplyButtons.value) {
      state.responseMessageText = null;
      state.followUpMessageText = null;
      state.followUpDelayMinutes = 60;
    }
  }
);

// Reset follow-up when response message is cleared
watch(
  () => state.responseMessageText,
  (newValue) => {
    if (!newValue) {
      state.followUpMessageText = null;
      state.followUpDelayMinutes = 60;
    }
  }
);

// Watch for plan selection changes
watch(
  () => state.selectedPlanId,
  (planId) => {
    if (planId) {
      const plan = selectedPlan.value;
      if (plan) {
        // Popular TODOS os campos com dados do plano (mesmo ocultos)
        // Isso garante que a validação passe
        state.title = plan.name;
        state.inboxId = plan.inbox_id;
        state.actualVolume = plan.default_volume;
        state.scheduledAt = currentDateTime.value;
        
        // Popular campos ocultos para passar validação
        // Extrair IDs das labels do público
        state.selectedAudience = plan.audience?.map(a => a.id) || [];
        
        // Popular templateId (simular seleção)
        // Usamos um ID fictício já que o template será carregado do plano
        state.templateId = plan.template_params?.name || 'from_plan';
        
        // Resposta automática do plano
        state.responseMessageText = plan.template_params?.response_message_text || null;
      }
    } else {
      // Limpar campos ao voltar para "Novo Plano"
      if (formState.inboxDefault.value?.id) {
        state.inboxId = formState.inboxDefault.value.id;
      }
      state.actualVolume = 100;
      state.title = '';
      state.templateId = null;
      state.selectedAudience = [];
      state.responseMessageText = null;
    }
  }
);

// Load campaign plans on mount
onMounted(() => {
  store.dispatch('campaignPlans/get');
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Seleção de Plano de Campanha -->
    <div class="flex flex-col gap-1">
      <label for="campaign-plan" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.PLAN_SELECTION.LABEL') }}
      </label>
      <ComboBox
        id="campaign-plan"
        v-model="state.selectedPlanId"
        :options="campaignPlanOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.PLAN_SELECTION.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Campos para Novo Plano -->
    <template v-if="!isUsingPlan">
      <Input
        v-model="state.title"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
        :message="formErrors.title"
        :message-type="formErrors.title ? 'error' : 'info'"
      />

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <!-- Mensagem de Resposta (apenas se template principal tiver botões) -->
    <div v-if="hasQuickReplyButtons" class="flex flex-col gap-1">
      <TextArea
        id="responseMessageText"
        v-model="state.responseMessageText"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.RESPONSE_MESSAGE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.RESPONSE_MESSAGE.PLACEHOLDER')"
        auto-height
        min-height="8rem"
        max-height="20rem"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.RESPONSE_MESSAGE.INFO') }}
      </p>
    </div>

    <!-- Mensagem de Follow-up (apenas se Response Message estiver preenchida) -->
    <div v-if="hasQuickReplyButtons && state.responseMessageText" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <TextArea
          id="followUpMessageText"
          v-model="state.followUpMessageText"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FOLLOW_UP_MESSAGE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FOLLOW_UP_MESSAGE.PLACEHOLDER')"
          auto-height
          min-height="6rem"
          max-height="15rem"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.FOLLOW_UP_MESSAGE.INFO') }}
        </p>
      </div>

      <!-- Campo de tempo de espera (apenas se follow-up message estiver preenchida) -->
      <div v-if="state.followUpMessageText" class="flex flex-col gap-1">
        <Input
          id="followUpDelayMinutes"
          v-model.number="state.followUpDelayMinutes"
          type="number"
          min="1"
          max="1440"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FOLLOW_UP_DELAY.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.FOLLOW_UP_DELAY.PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.FOLLOW_UP_DELAY.INFO') }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    </template>

    <!-- Campos para Plano Selecionado -->
    <template v-if="isUsingPlan">
      <Input
        v-model.number="state.actualVolume"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.ACTUAL_VOLUME.LABEL')"
        type="number"
        min="1"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.ACTUAL_VOLUME.PLACEHOLDER')"
      />
    </template>

    <!-- Data/Hora sempre visível -->
    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
